import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { EmailSectionLayout } from '../../components/EmailSectionLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Send, Plus, Loader2, Trash2, Edit2, BarChart3, Mail, Layers } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';

const STATUS_TINT = {
  draft: 'bg-zinc-100 text-zinc-600',
  scheduled: 'bg-sky-100 text-sky-700',
  sending: 'bg-amber-100 text-amber-700',
  active: 'bg-violet-100 text-violet-700',
  sent: 'bg-emerald-100 text-emerald-700',
  paused: 'bg-zinc-200 text-zinc-600',
};

export const EmailCampaignsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showChooser, setShowChooser] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/email/campaigns');
      setCampaigns(r.data?.campaigns || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const remove = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"? This also clears its send history.`)) return;
    try { await api.delete(`/email/campaigns/${c.id}`); toast.success('Deleted'); await load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <EmailSectionLayout
      actions={
        <Button onClick={() => setShowChooser(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-campaign-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New campaign
        </Button>
      }
    >
      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>
      ) : campaigns.length === 0 ? (
        <Card data-testid="campaigns-empty"><CardContent className="py-14 text-center text-zinc-500">
          <Send className="w-10 h-10 mx-auto text-zinc-300 mb-3" />
          <p className="font-medium text-zinc-700">No campaigns yet</p>
          <p className="text-sm mb-4">Write your first plain-text broadcast.</p>
          <Button onClick={() => setShowChooser(true)} className="bg-indigo-600 hover:bg-indigo-700"><Plus className="w-4 h-4 mr-1.5" /> New campaign</Button>
        </CardContent></Card>
      ) : (
        <div className="space-y-3" data-testid="campaigns-list">
          {campaigns.map((c) => (
            <Card key={c.id} className="hover:shadow-sm transition-shadow" data-testid={`campaign-row-${c.id}`}>
              <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className={`w-9 h-9 rounded-lg ${c.type === 'sequence' ? 'bg-violet-100 text-violet-600' : 'bg-indigo-100 text-indigo-600'} flex items-center justify-center shrink-0`}>{c.type === 'sequence' ? <Layers className="w-4 h-4" /> : <Mail className="w-4 h-4" />}</div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-zinc-900 truncate">{c.name}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_TINT[c.status] || 'bg-zinc-100'}`}>{c.status}</span>
                    </div>
                    <div className="text-sm text-zinc-500 truncate">{c.type === 'sequence' ? `${(c.steps || []).length} step${(c.steps || []).length === 1 ? '' : 's'} · ${c.enrollment_count || 0} enrolled` : (c.subject || <span className="italic">No subject yet</span>)}</div>
                    <div className="text-xs text-zinc-400 mt-0.5">
                      {c.counts?.total > 0
                        ? `${c.counts.sent + c.counts.delivered + c.counts.opened + c.counts.clicked} sent · ${c.counts.opened + c.counts.clicked} opened · ${c.counts.pending} pending`
                        : 'Not sent yet'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Button variant="outline" size="sm" onClick={() => navigate(`/email/campaigns/${c.id}/stats`)} data-testid={`stats-${c.id}`}><BarChart3 className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Stats</span></Button>
                  <Button variant="outline" size="sm" onClick={() => navigate(`/email/campaigns/${c.id}/edit`)} data-testid={`edit-campaign-${c.id}`}><Edit2 className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Edit</span></Button>
                  <button onClick={() => remove(c)} className="p-2 text-zinc-400 hover:text-red-600" data-testid={`delete-campaign-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showChooser} onOpenChange={setShowChooser}>
        <DialogContent data-testid="campaign-type-dialog">
          <DialogHeader><DialogTitle>What do you want to create?</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
            <button onClick={() => navigate('/email/campaigns/new?type=broadcast')} className="text-left p-5 rounded-xl border border-zinc-200 hover:border-indigo-400 hover:shadow-sm transition-all" data-testid="choose-broadcast">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center mb-3"><Mail className="w-5 h-5" /></div>
              <div className="font-semibold text-zinc-900">Broadcast</div>
              <div className="text-sm text-zinc-500 mt-1">A one-time email sent now or scheduled for later.</div>
            </button>
            <button onClick={() => navigate('/email/campaigns/new?type=sequence')} className="text-left p-5 rounded-xl border border-zinc-200 hover:border-violet-400 hover:shadow-sm transition-all" data-testid="choose-sequence">
              <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center mb-3"><Layers className="w-5 h-5" /></div>
              <div className="font-semibold text-zinc-900">Drip sequence</div>
              <div className="text-sm text-zinc-500 mt-1">A series of timed emails that auto-send after a subscriber enrolls.</div>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </EmailSectionLayout>
  );
};

export default EmailCampaignsPage;
